import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  User, 
  Mail, 
  Smartphone, 
  Globe, 
  Clock, 
  Shield, 
  RefreshCcw, 
  Copy, 
  LogOut,
  ChevronDown,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { toast } from '../hooks/use-toast';
import { ensureLoggedIn, loadProfile, getTokens, getDeviceInfo, doLogout } from '../services/liff';
import { formatDisplayName, getInitials, formatDateTimeTH } from '../utils/textUtils';

const ProfilePage = () => {
  const [profile, setProfile] = useState(null);
  const [tokens, setTokens] = useState(null);
  const [deviceInfo, setDeviceInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedSections, setExpandedSections] = useState({});

  const fetchAllData = async () => {
    try {
      setIsLoading(true);
      
      // Step 1: Ensure we're logged in and have valid tokens
      const ok = await ensureLoggedIn();
      if (!ok) return; // redirected to login

      // Step 2: Load profile from LIFF SDK (no REST call needed)
      const userProfile = await loadProfile();
      setProfile(userProfile);

      // Step 3: Get tokens (accessToken for LINE REST, idToken for backend OIDC)
      const userTokens = getTokens();
      setTokens(userTokens);

      // Step 4: Get device info
      const userDeviceInfo = getDeviceInfo();
      setDeviceInfo(userDeviceInfo);

      // Step 5: Save tokens to localStorage for debugging
      localStorage.setItem('liff_tokens', JSON.stringify({
        ...userTokens,
        timestamp: new Date().toISOString()
      }));

      // OPTIONAL: Verify with backend if VERIFY_ENDPOINT is configured
      // const VERIFY_ENDPOINT = process.env.REACT_APP_VERIFY_ENDPOINT;
      // if (VERIFY_ENDPOINT) {
      //   await verifyWithServer(VERIFY_ENDPOINT);
      // }

    } catch (error) {
      console.error('Profile fetch failed:', error);
      window.location.replace('/error?m=' + encodeURIComponent(String(error)));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const handleRefresh = async () => {
    await fetchAllData();
    toast({
      title: "Profile refreshed",
      description: "Your profile data has been updated successfully.",
    });
  };

  const handleCopyJSON = (data, title) => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast({
      title: "Copied to clipboard",
      description: `${title} data copied successfully.`,
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('liff_tokens');
    doLogout(); // This will call liff.logout() and redirect to /
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (timestamp) => {
    // Convert Unix timestamp to Date (already in correct timezone)
    // Unix timestamp is in seconds, JavaScript Date expects milliseconds
    const date = new Date(timestamp * 1000);
    
    // Unix timestamp is already in UTC, so we can format it directly with Bangkok timezone
    return date.toLocaleString('th-TH', {
      timeZone: 'Asia/Bangkok',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-cyan-100 via-blue-50 to-white font-prompt">
        <Card className="w-full max-w-md shadow-xl border-0">
          <CardContent className="p-6 text-center">
            <Loader2 className="w-8 h-8 mx-auto text-blue-500 animate-spin mb-4" />
            <h3 className="font-semibold text-gray-800">กำลังโหลดโปรไฟล์...</h3>
            <p className="text-sm text-gray-500 mt-1">กำลังดึงข้อมูล LINE ของคุณอย่างปลอดภัย</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-100 via-blue-50 to-white p-4 font-prompt">
      <div className="max-w-md mx-auto space-y-4">
        {/* Profile Header */}
        <Card className="shadow-xl border-0">
          <CardContent className="p-6 text-center">
            <Avatar className="w-24 h-24 mx-auto mb-4 border-4 border-blue-100">
              <AvatarImage src={profile?.pictureUrl} alt={formatDisplayName(profile?.displayName)} />
              <AvatarFallback className="bg-blue-500 text-white text-xl">
                {getInitials(profile?.displayName)}
              </AvatarFallback>
            </Avatar>
            <h1 className="text-xl font-bold text-gray-800 mb-1">
              {formatDisplayName(profile?.displayName)}
            </h1>
            <p className="text-sm text-gray-500 mb-4">
              {profile?.statusMessage || 'ไม่มีข้อความสถานะ'}
            </p>
            <div className="flex justify-center gap-2">
              <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                <User className="w-3 h-3 mr-1" />
                {profile?.userId?.substring(0, 8)}...
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Basic Info */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              ข้อมูลโปรไฟล์
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">รหัสผู้ใช้</span>
              <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                {profile?.userId?.substring(0, 12)}...
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">ชื่อที่แสดง</span>
              <span className="text-sm font-medium">{formatDisplayName(profile?.displayName)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">รูปโปรไฟล์</span>
              <Badge variant={profile?.pictureUrl ? "default" : "secondary"}>
                {profile?.pictureUrl ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                {profile?.pictureUrl ? 'มี' : 'ไม่มี'}
              </Badge>
            </div>
          </CardContent>
        </Card>

        {/* Token Information */}
        <Card className="shadow-xl border-0">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="w-5 h-5 text-blue-500" />
              สถานะการพิสูจน์ตัวตน
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Access Token</span>
              <Badge variant={tokens?.accessToken ? "default" : "secondary"}>
                {tokens?.accessToken ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                {tokens?.accessToken ? 'ใช้ได้' : 'ไม่มี'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">ID Token</span>
              <Badge variant={tokens?.idToken ? "default" : "secondary"}>
                {tokens?.idToken ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
                {tokens?.idToken ? 'ใช้ได้' : 'ไม่มี'}
              </Badge>
            </div>
            <div className="text-xs text-gray-500 mt-2 p-2 bg-blue-50 rounded">
              <strong>การใช้งาน Token:</strong><br/>
              • Access Token: สำหรับเรียก LINE API<br/>
              • ID Token: สำหรับการตรวจสอบกับระบบหลังบ้าน
            </div>
          </CardContent>
        </Card>

        {/* Token Claims */}
        {tokens?.decoded && (
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                ข้อมูล ID Token Claims
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {tokens.decoded.email && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">อีเมล</span>
                  <span className="text-sm font-medium">{tokens.decoded.email}</span>
                </div>
              )}
              {tokens.decoded.name && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">ชื่อ</span>
                  <span className="text-sm font-medium">{tokens.decoded.name}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Subject</span>
                <span className="text-sm font-mono bg-gray-100 px-2 py-1 rounded">
                  {tokens.decoded.sub?.substring(0, 12)}...
                </span>
              </div>
              {tokens.decoded.exp && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500">หมดอายุ</span>
                  <span className="text-sm font-medium">{formatDate(tokens.decoded.exp)}</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Device Info */}
        {deviceInfo && (
          <Card className="shadow-xl border-0">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-blue-500" />
                ข้อมูลอุปกรณ์
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">แพลตฟอร์ม</span>
                <span className="text-sm font-medium">{deviceInfo.os}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">ภาษา</span>
                <span className="text-sm font-medium">{deviceInfo.language}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">เวอร์ชัน LIFF</span>
                <span className="text-sm font-medium">{deviceInfo.version}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">ใน LINE App</span>
                <Badge variant={deviceInfo.isInClient ? "default" : "secondary"}>
                  {deviceInfo.isInClient ? 'ใช่' : 'ไม่'}
                </Badge>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expandable JSON Sections */}
        <div className="space-y-2">
          {/* ID Token Data */}
          <Collapsible 
            open={expandedSections.idToken} 
            onOpenChange={() => toggleSection('idToken')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-blue-500" />
                      ข้อมูล ID Token (สำหรับตรวจสอบระบบหลังบ้าน)
                    </span>
                    {expandedSections.idToken ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(tokens?.decoded, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(tokens?.decoded, 'ID Token')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    คัดลอก JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Profile Data */}
          <Collapsible 
            open={expandedSections.profile} 
            onOpenChange={() => toggleSection('profile')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <User className="w-4 h-4 text-blue-500" />
                      ข้อมูลโปรไฟล์ (จาก LIFF SDK)
                    </span>
                    {expandedSections.profile ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(profile, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(profile, 'Profile')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    คัดลอก JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Token Details */}
          <Collapsible 
            open={expandedSections.tokens} 
            onOpenChange={() => toggleSection('tokens')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      รายละเอียด Token
                    </span>
                    {expandedSections.tokens ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="space-y-3">
                    <div className="bg-blue-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-blue-700 mb-1">Access Token (สำหรับ LINE API):</p>
                      <p className="text-xs font-mono text-blue-600 break-all">
                        {tokens?.accessToken ? `${tokens.accessToken.substring(0, 50)}...` : 'ไม่มี'}
                      </p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3">
                      <p className="text-xs font-semibold text-green-700 mb-1">ID Token (สำหรับ OIDC ระบบหลังบ้าน):</p>
                      <p className="text-xs font-mono text-green-600 break-all">
                        {tokens?.idToken ? `${tokens.idToken.substring(0, 50)}...` : 'ไม่มี'}
                      </p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(tokens, 'All Tokens')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    คัดลอกข้อมูล Token
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Device Data */}
          <Collapsible 
            open={expandedSections.device} 
            onOpenChange={() => toggleSection('device')}
          >
            <Card className="shadow-lg border-0">
              <CollapsibleTrigger asChild>
                <CardHeader className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-blue-500" />
                      ข้อมูลอุปกรณ์
                    </span>
                    {expandedSections.device ? 
                      <ChevronDown className="w-4 h-4" /> : 
                      <ChevronRight className="w-4 h-4" />
                    }
                  </CardTitle>
                </CardHeader>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="pt-0">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-words">
                      {JSON.stringify(deviceInfo, null, 2)}
                    </pre>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="mt-3 w-full" 
                    onClick={() => handleCopyJSON(deviceInfo, 'Device Info')}
                  >
                    <Copy className="w-3 h-3 mr-2" />
                    คัดลอก JSON
                  </Button>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-3 gap-2 pt-2">
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            className="flex items-center gap-1"
          >
            <RefreshCcw className="w-3 h-3" />
            รีเฟรช
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleCopyJSON({ profile, tokens, deviceInfo }, 'All Data')}
            className="flex items-center gap-1"
          >
            <Copy className="w-3 h-3" />
            คัดลอกทั้งหมด
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleLogout}
            className="flex items-center gap-1 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <LogOut className="w-3 h-3" />
            ออกจากระบบ
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-gray-400 py-4 space-y-1">
          <p>ขับเคลื่อนโดย LINE LIFF Platform</p>
          <p>Tokens ได้รับการรักษาความปลอดภัย • ข้อมูลอัปเดต: {formatDateTimeTH(new Date())}</p>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;